import { Circle } from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Box, TabProps } from '@mui/material';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActionBar, Header } from 'src/components/header';
import { IconTypography } from 'src/components/iconTypography/IconTypography';
import { PublicationModal } from 'src/components/modal';
import { Progress } from 'src/components/progress';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import {
  LOCATION_HEADER_HEIGHT,
  LOCATION_HEADER_LOCATION_GROUP_TAB_INFO_HEIGHT,
} from 'src/constants';
import { PATHS } from 'src/constants/paths';
import { CalendarView } from 'src/features/locations/calendarLocationGroupPage/modules/CalendarView/CalendarView';
import { ActiveDayIndicators } from 'src/features/locations/calendarLocationPage/modules/ActiveDayIndicators';
import { StyledTab } from 'src/features/locations/scheduleLocationPageOO/modules/OOLocationSchedulePageTable/OOLocationSchedulePageTable.styled';
import { useParamQuery } from 'src/hooks';
import { lang, theme, useUserRole } from 'src/utils';

import { DeleteBundleAction } from '../calendarLocationGroupPage/modules/CalendarView/eventContentSidebar/EventContentSidebar.interface';
import { DaypartDisabledAlert } from '../modules/DaypartDisabledAlert';
import {
  ButtonGroupWrapper,
  CalendarContainer,
  Container,
  HeaderContainer,
  StyledCalendarLegendWrapper,
  StyledIconButton,
  Tabs,
} from './CalendarLocationPage.styled';
import { useCalendarLocationPage } from './hooks/CalendarLocationPage.hook';
import { LocationCalendarHeader } from './modules/CalendarLocationHeader/LocationCalendarHeader';

export const Tab = (props: TabProps) => {
  return (
    <CustomTooltip
      width={500}
      placement="bottom"
      title={props.label}
      overflowWidth={360}>
      <StyledTab {...props} />
    </CustomTooltip>
  );
};

// Individual location views always represent exactly one location.
// The locationsCount guard in EventContentSidebar requires a positive value
// to allow the publication modal to open after delete actions.
const INDIVIDUAL_LOCATION_COUNT = 1;

export const CalendarLocationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { locationCode = '' } = useParams();
  const { getParamsValue } = useParamQuery();
  const { isOwnerOperator } = useUserRole();
  const location_group_id = getParamsValue('groupID').join();
  const isCalendarActive = location?.pathname?.includes('/calendar');

  //NOTE: This is set to '1' as default for Breakfast in the UI.
  const [selectedDaypartId, setSelectedDaypartId] = useState<string>('1');

  const {
    projectConfig,
    locationData,
    isLocationLoading,
    onChangeLocationGroup,
    publishModal,
    locationGroup,
    locationGroups,
    isLoadingActiveDayparts,
    activeDaypartOptions,
    excludeScheduleLocation,
    activeDays,
    isActiveDayVisible,
    handleChangeViewType,
    alertVisible,
    setAlertVisible,
  } = useCalendarLocationPage();

  return isLocationLoading ? (
    <Progress />
  ) : (
    <Container>
      <Header
        height={LOCATION_HEADER_HEIGHT}
        title={locationData?.hq?.name}
        actionBar={
          <ActionBar
            isButtonHidden={true}
            showViewOnly
            type={lang.scheduleComponent.label}
          />
        }
        breadcrumbItems={[
          {
            label: lang.locationGroup,
            link: !isOwnerOperator
              ? PATHS.LOCATION_GROUPS
              : PATHS.OO_LOCATION_PAGE,
          },
          {
            label: lang.schedules,
          },
        ]}
      />

      <DaypartDisabledAlert
        locationGroupId={Number(location_group_id)}
        locationCode={locationCode}
        selectedDaypartId={Number(selectedDaypartId)}
        navigateTo={PATHS.LOCATION_TIMINGS(locationCode, location_group_id)}
        onVisibilityChange={setAlertVisible}
      />

      {!isEmpty(locationGroups) && (
        <Tabs
          value={Number(location_group_id)}
          onChange={onChangeLocationGroup}
          variant="scrollable"
          scrollButtons="auto">
          {locationGroups?.map((item) => (
            <Tab
              disableRipple
              key={item.id}
              value={item?.id}
              label={item?.name}
            />
          ))}
        </Tabs>
      )}
      <Box height={LOCATION_HEADER_LOCATION_GROUP_TAB_INFO_HEIGHT}>
        <LocationCalendarHeader
          selectedLocationGroup={locationData?.locationGroups?.find(
            (group) => group.id === Number(location_group_id),
          )}
        />
      </Box>
      <HeaderContainer>
        <ButtonGroupWrapper>
          <StyledIconButton $active={!!isCalendarActive}>
            <CalendarTodayIcon />
          </StyledIconButton>
          <StyledIconButton
            onClick={() =>
              navigate({
                pathname: PATHS.LOCATION_SCHEDULE(locationCode),
                search: `groupID=${location_group_id}`,
              })
            }>
            <FormatListBulletedIcon />
          </StyledIconButton>
        </ButtonGroupWrapper>
        <StyledCalendarLegendWrapper>
          {isActiveDayVisible && (
            <ActiveDayIndicators activeDays={activeDays} />
          )}
          <IconTypography
            icon={<Circle />}
            text={lang.default}
            textColor={theme.palette.brand.black}
            iconSize="12px"
            iconColor={theme.palette.brand.mediumGrey}
          />
          <IconTypography
            icon={<Circle />}
            text={lang.scheduled}
            textColor={theme.palette.brand.black}
            iconSize="12px"
            iconColor={theme.palette.primary.light}
          />
        </StyledCalendarLegendWrapper>
      </HeaderContainer>
      <CalendarContainer $alertVisible={alertVisible}>
        <CalendarView
          projectConfig={projectConfig}
          isCalendarViewGroup={false}
          locationGroup={locationGroup}
          shouldShowCalendar={
            !isLoadingActiveDayparts && activeDaypartOptions.length > 0
          }
          deleteBundleAction={excludeScheduleLocation as DeleteBundleAction}
          viewTypeChangeCallback={handleChangeViewType}
          selectedDaypartId={selectedDaypartId}
          handleSelectedDaypartIdChange={setSelectedDaypartId}
          locationData={locationData}
          locationsCount={INDIVIDUAL_LOCATION_COUNT}
        />
      </CalendarContainer>
      {publishModal.open && (
        <PublicationModal
          open={publishModal.open}
          locationCodes={[locationCode]}
          onClose={publishModal.onCloseModal}
        />
      )}
    </Container>
  );
};
